import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import DraftControls from '../components/DraftControls';
import PageSectionsManager from '../components/PageSectionsManager';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Download, Search, CheckCircle, Circle, Trash2, Edit, X, Save,
  Users, FileText, Settings, RefreshCw, Eye, EyeOff, Plus, Minus,
  GripVertical, ChevronDown, ChevronUp, Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_GOLF_SECTIONS = ['hero', 'intro', 'why', 'form', 'status'];

/* Sortable section wrapper with visual drag feedback */
const SortableSection = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease, box-shadow 200ms ease',
    opacity: isDragging ? 0.6 : 1,
    position: 'relative',
    zIndex: isDragging ? 50 : 'auto',
    scale: isDragging ? '1.01' : '1',
    boxShadow: isDragging ? '0 12px 40px rgba(0,69,127,0.15)' : 'none',
    borderRadius: '0.5rem',
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`sortable-section-${id}`}>
      {isOver && !isDragging && (
        <div className="absolute -top-1 left-4 right-4 h-0.5 bg-[#00457f] rounded-full z-20" />
      )}
      <div className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing z-10 rounded-l-lg hover:bg-slate-100 transition-colors" {...attributes} {...listeners} data-testid={`drag-handle-${id}`}>
        <GripVertical className="w-4 h-4 text-gray-300 hover:text-gray-500" />
      </div>
      <div className="ml-6">{children}</div>
    </div>
  );
};

const SECTION_ICONS = {
  hero: FileText,
  intro: FileText,
  why: FileText,
  form: Settings,
  status: Eye,
};

const SECTION_DEFAULTS = {
  hero: 'Hero Section',
  intro: 'Introduction',
  why: 'Why This Event',
  form: 'Form Settings',
  status: 'Page Status',
};

const AdminGolf = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inquiries');
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterParticipation, setFilterParticipation] = useState('');
  const [filterContacted, setFilterContacted] = useState('');
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [contentForm, setContentForm] = useState({});
  const [expanded, setExpanded] = useState({});
  const [editingTitle, setEditingTitle] = useState(null);
  const [titleDraft, setTitleDraft] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sectionOrder = useMemo(
    () => contentForm?.section_order || DEFAULT_GOLF_SECTIONS,
    [contentForm?.section_order]
  );

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inquiriesRes, statsRes, contentRes] = await Promise.all([
        fetch(`${API_URL}/api/golf/inquiries`),
        fetch(`${API_URL}/api/golf/stats`),
        fetch(`${API_URL}/api/golf/content`)
      ]);
      if (inquiriesRes.ok) setInquiries(await inquiriesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (contentRes.ok) {
        const d = await contentRes.json();
        setContent(d);
        setContentForm(d);
      }
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleExportCSV = () => { window.open(`${API_URL}/api/golf/inquiries/export/csv`, '_blank'); };

  const handleUpdateInquiry = async (id, updates) => {
    try {
      const r = await fetch(`${API_URL}/api/golf/inquiries/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
      if (r.ok) { toast.success('Inquiry updated'); fetchData(); setEditingInquiry(null); }
    } catch { toast.error('Failed to update inquiry'); }
  };

  const handleDeleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      const r = await fetch(`${API_URL}/api/golf/inquiries/${id}`, { method: 'DELETE' });
      if (r.ok) { toast.success('Deleted'); fetchData(); }
    } catch { toast.error('Failed to delete'); }
  };

  const handleSaveContent = async () => {
    try {
      const r = await fetch(`${API_URL}/api/golf/content`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(contentForm) });
      if (r.ok) { toast.success('Content saved'); setContent(contentForm); }
    } catch { toast.error('Failed to save'); }
  };

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sectionOrder.indexOf(active.id);
    const newIdx = sectionOrder.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const newOrder = arrayMove(sectionOrder, oldIdx, newIdx);
    setContentForm(prev => ({ ...prev, section_order: newOrder }));
  }, [sectionOrder]);

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  const getSectionTitle = (key) => contentForm?.[`section_title_${key}`] || SECTION_DEFAULTS[key];
  const isSectionHidden = (key) => !!contentForm?.[`section_hidden_${key}`];
  const toggleSectionVisibility = (key, e) => {
    e.stopPropagation();
    setContentForm(prev => ({ ...prev, [`section_hidden_${key}`]: !prev[`section_hidden_${key}`] }));
  };
  const startTitleEdit = (key, e) => { e.stopPropagation(); setEditingTitle(key); setTitleDraft(getSectionTitle(key)); };
  const saveTitleEdit = () => { if (editingTitle && titleDraft.trim()) setContentForm(prev => ({ ...prev, [`section_title_${editingTitle}`]: titleDraft.trim() })); setEditingTitle(null); };

  const addWhyPoint = () => setContentForm(prev => ({ ...prev, why_points: [...(prev.why_points || []), ''] }));
  const removeWhyPoint = (index) => setContentForm(prev => ({ ...prev, why_points: prev.why_points.filter((_, i) => i !== index) }));
  const updateWhyPoint = (index, value) => setContentForm(prev => ({ ...prev, why_points: prev.why_points.map((p, i) => i === index ? value : p) }));

  const participationLabels = { 'help_joining_team': 'Need help joining team', 'have_four_person_team': 'Have four-person team', 'want_more_details': 'Want more details' };
  const sponsorshipLabels = { 'entry_level': 'Entry Level', 'mid_level': 'Mid Level', 'title_sponsor': 'Title Sponsor', 'not_at_this_time': 'Not interested' };

  const filteredInquiries = inquiries.filter(inquiry => {
    const matchesSearch = !searchTerm || inquiry.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || inquiry.email?.toLowerCase().includes(searchTerm.toLowerCase()) || inquiry.organization?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesParticipation = !filterParticipation || inquiry.participation_interest === filterParticipation;
    const matchesContacted = filterContacted === '' || (filterContacted === 'contacted' && inquiry.contacted) || (filterContacted === 'not_contacted' && !inquiry.contacted);
    return matchesSearch && matchesParticipation && matchesContacted;
  });

  const renderSectionHeader = (key) => {
    const Icon = SECTION_ICONS[key] || FileText;
    const title = getSectionTitle(key);
    const isEditing = editingTitle === key;
    const hidden = isSectionHidden(key);
    return (
      <div className={`w-full flex items-center justify-between p-4 transition-colors rounded-t-lg ${hidden ? 'bg-slate-100/60' : 'bg-slate-50 hover:bg-slate-100'}`}>
        <button onClick={() => toggle(key)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          <Icon className={`w-5 h-5 shrink-0 ${hidden ? 'text-gray-300' : 'text-[#00457f]'}`} />
          {isEditing ? (
            <form className="flex items-center gap-2 flex-1" onSubmit={(e) => { e.preventDefault(); saveTitleEdit(); }} onClick={(e) => e.stopPropagation()}>
              <input autoFocus value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} onBlur={saveTitleEdit} className="flex-1 px-2 py-1 text-sm font-semibold border rounded-md bg-white outline-none focus:ring-2 focus:ring-[#00457f]" />
            </form>
          ) : (
            <span className={`font-semibold truncate ${hidden ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{title}</span>
          )}
          {hidden && <span className="px-1.5 py-0.5 bg-gray-200 text-gray-500 text-[10px] font-bold rounded uppercase tracking-wider shrink-0">Hidden</span>}
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={(e) => toggleSectionVisibility(key, e)}
            className={`p-1.5 rounded-lg transition-colors ${hidden ? 'text-red-400 hover:text-red-600 hover:bg-red-50' : 'text-green-500 hover:text-green-700 hover:bg-green-50'}`}
            title={hidden ? 'Show on public page' : 'Hide from public page'}
            data-testid={`toggle-vis-${key}`}
          >
            {hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          {!isEditing && (
            <button onClick={(e) => startTitleEdit(key, e)} className="p-1.5 text-gray-400 hover:text-[#00457f] hover:bg-blue-50 rounded-lg transition-colors" title="Edit title"><Pencil className="w-3.5 h-3.5" /></button>
          )}
          <button onClick={() => toggle(key)} className="p-1">{expanded[key] ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}</button>
        </div>
      </div>
    );
  };

  const renderSectionContent = (key) => {
    switch (key) {
      case 'hero':
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {renderSectionHeader('hero')}
            {expanded.hero && (
              <div className="p-4 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Title</Label><Input value={contentForm.hero_title || ''} onChange={(e) => setContentForm(prev => ({ ...prev, hero_title: e.target.value }))} /></div>
                  <div><Label>Tagline</Label><Input value={contentForm.hero_tagline || ''} onChange={(e) => setContentForm(prev => ({ ...prev, hero_tagline: e.target.value }))} /></div>
                </div>
                <div><Label>Subtitle</Label><Textarea value={contentForm.hero_subtitle || ''} onChange={(e) => setContentForm(prev => ({ ...prev, hero_subtitle: e.target.value }))} rows={2} /></div>
              </div>
            )}
          </div>
        );
      case 'intro':
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {renderSectionHeader('intro')}
            {expanded.intro && (
              <div className="p-4 space-y-4">
                <div><Label>Notice Banner</Label><Textarea value={contentForm.intro_notice || ''} onChange={(e) => setContentForm(prev => ({ ...prev, intro_notice: e.target.value }))} rows={2} /></div>
                <div><Label>Intro Text</Label><Textarea value={contentForm.intro_text || ''} onChange={(e) => setContentForm(prev => ({ ...prev, intro_text: e.target.value }))} rows={4} /></div>
              </div>
            )}
          </div>
        );
      case 'why':
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {renderSectionHeader('why')}
            {expanded.why && (
              <div className="p-4 space-y-4">
                <div><Label>Section Title</Label><Input value={contentForm.why_title || ''} onChange={(e) => setContentForm(prev => ({ ...prev, why_title: e.target.value }))} /></div>
                <div>
                  <Label>Key Points</Label>
                  <div className="space-y-2">
                    {contentForm.why_points?.map((point, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input value={point} onChange={(e) => updateWhyPoint(idx, e.target.value)} placeholder="Enter point..." />
                        <Button onClick={() => removeWhyPoint(idx)} variant="outline" size="icon" className="flex-shrink-0"><Minus className="w-4 h-4" /></Button>
                      </div>
                    ))}
                    <Button onClick={addWhyPoint} variant="outline" size="sm"><Plus className="w-4 h-4 mr-1" />Add Point</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'form':
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {renderSectionHeader('form')}
            {expanded.form && (
              <div className="p-4 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Form Title</Label><Input value={contentForm.form_title || ''} onChange={(e) => setContentForm(prev => ({ ...prev, form_title: e.target.value }))} /></div>
                  <div><Label>Contact Email</Label><Input value={contentForm.contact_email || ''} onChange={(e) => setContentForm(prev => ({ ...prev, contact_email: e.target.value }))} /></div>
                </div>
                <div><Label>Confirmation Message</Label><Textarea value={contentForm.confirmation_message || ''} onChange={(e) => setContentForm(prev => ({ ...prev, confirmation_message: e.target.value }))} rows={2} /></div>
              </div>
            )}
          </div>
        );
      case 'status':
        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {renderSectionHeader('status')}
            {expanded.status && (
              <div className="p-4">
                <div className="flex items-center gap-4">
                  <Label>Page Active</Label>
                  <button
                    onClick={() => setContentForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                    className={`flex items-center gap-2 px-3 py-1 rounded ${contentForm.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {contentForm.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    {contentForm.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      default: return null;
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-4">
          <DraftControls draftKey="golf" pageLabel="Golf" previewPath="/golf-outing" />
        </div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Golf Outing Management</h1>
            <p className="text-gray-600">Manage interest inquiries and page content</p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card><CardContent className="p-4"><p className="text-sm text-gray-600">Total Inquiries</p><p className="text-2xl font-bold text-[#00457f]">{stats.total}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-sm text-gray-600">Contacted</p><p className="text-2xl font-bold text-green-600">{stats.contacted}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-sm text-gray-600">Not Contacted</p><p className="text-2xl font-bold text-amber-600">{stats.not_contacted}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-sm text-gray-600">Have Teams</p><p className="text-2xl font-bold text-purple-600">{stats.by_participation?.have_four_person_team || 0}</p></CardContent></Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <button onClick={() => setActiveTab('inquiries')} className={`px-4 py-2 font-medium transition-colors ${activeTab === 'inquiries' ? 'text-[#00457f] border-b-2 border-[#00457f]' : 'text-gray-600 hover:text-gray-900'}`}>
            <Users className="w-4 h-4 inline mr-2" />Inquiries ({inquiries.length})
          </button>
          <button onClick={() => setActiveTab('content')} className={`px-4 py-2 font-medium transition-colors ${activeTab === 'content' ? 'text-[#00457f] border-b-2 border-[#00457f]' : 'text-gray-600 hover:text-gray-900'}`}>
            <FileText className="w-4 h-4 inline mr-2" />Page Content
          </button>
        </div>

        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <div>
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Search by name, email, or organization..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                </div>
              </div>
              <select value={filterParticipation} onChange={(e) => setFilterParticipation(e.target.value)} className="px-3 py-2 border rounded-md text-sm">
                <option value="">All Interest Types</option>
                <option value="help_joining_team">Need help joining team</option>
                <option value="have_four_person_team">Have four-person team</option>
                <option value="want_more_details">Want more details</option>
              </select>
              <select value={filterContacted} onChange={(e) => setFilterContacted(e.target.value)} className="px-3 py-2 border rounded-md text-sm">
                <option value="">All Status</option>
                <option value="contacted">Contacted</option>
                <option value="not_contacted">Not Contacted</option>
              </select>
              <Button onClick={handleExportCSV} variant="outline"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
            </div>

            <Card>
              <CardContent className="p-0">
                {filteredInquiries.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No inquiries found</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interest</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sponsorship</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredInquiries.map((inquiry) => (
                          <tr key={inquiry.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <button onClick={() => handleUpdateInquiry(inquiry.id, { contacted: !inquiry.contacted })} className={`flex items-center gap-1 text-sm ${inquiry.contacted ? 'text-green-600' : 'text-gray-400'}`}>
                                {inquiry.contacted ? <CheckCircle className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <div><p className="font-medium text-gray-900">{inquiry.full_name}</p><p className="text-sm text-gray-600">{inquiry.email}</p>{inquiry.organization && <p className="text-xs text-gray-500">{inquiry.organization}</p>}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-gray-700">{participationLabels[inquiry.participation_interest] || inquiry.participation_interest}</span>
                              {inquiry.team_name && <p className="text-xs text-purple-600 mt-1">Team: {inquiry.team_name}</p>}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {inquiry.sponsorship_interests?.map((s, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">{sponsorshipLabels[s] || s}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{inquiry.created_at ? new Date(inquiry.created_at).toLocaleDateString() : '-'}</td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                <button onClick={() => setEditingInquiry({...inquiry})} className="p-2 text-gray-400 hover:text-[#00457f] hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
                                <button onClick={() => handleDeleteInquiry(inquiry.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Content Tab — sortable sections */}
        {activeTab === 'content' && contentForm && (
          <div className="space-y-4">
            <div className="flex justify-end mb-2">
              <Button onClick={handleSaveContent} size="sm">
                <Save className="w-4 h-4 mr-1" /> Save All Changes
              </Button>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sectionOrder} strategy={verticalListSortingStrategy}>
                {sectionOrder.map((key) => (
                  <SortableSection key={key} id={key}>
                    {renderSectionContent(key)}
                  </SortableSection>
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Edit Inquiry Modal */}
        {editingInquiry && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Edit Inquiry</CardTitle>
                <button onClick={() => setEditingInquiry(null)}><X className="w-5 h-5" /></button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div><p className="font-medium">{editingInquiry.full_name}</p><p className="text-sm text-gray-600">{editingInquiry.email}</p></div>
                <div><Label>Admin Notes</Label><Textarea value={editingInquiry.admin_notes || ''} onChange={(e) => setEditingInquiry(prev => ({ ...prev, admin_notes: e.target.value }))} rows={4} placeholder="Add notes..." /></div>
                <div className="flex gap-2 justify-end">
                  <Button onClick={() => setEditingInquiry(null)} variant="outline">Cancel</Button>
                  <Button onClick={() => handleUpdateInquiry(editingInquiry.id, { admin_notes: editingInquiry.admin_notes })}>Save Notes</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Additional Template Sections */}
      <div className="max-w-5xl mx-auto px-4 pb-8">
        <PageSectionsManager page="golf" title="Additional Sections" />
      </div>
    </AdminLayout>
  );
};

export default AdminGolf;
